import React, { ReactElement } from 'react';
import COLORS from '../../style/universal.color';
import {
    StrikeZoneRenderRectMatrixProps,
    StrikeZoneStyleProps,
    StrikeZoneRectMatrixProps,
    StrikeZoneSelectedProps,
} from './strikeZone.types';
import {
    StrikeZoneG,
    StrikeZoneRect,
    StrikeZoneText,
} from './strikeZone.style';
import {
    strikeZoneDataValueKey,
    strikeZoneDataTypeKey,
    strikeZoneSelectedClassName,
    strokeWidth,
} from './constants';

const darkStrikeZoneEmptyStrokeColor: string = '#E6E6E6';
const darkHomePlateFillColor: string = '#737373';
const {
    linkBlueOnDark,
    mlbGrey1,
    mlbGrey2,
    mlbGrey3,
    mlbGrey4,
    mlbGrey6,
    mlbGrey7,
    mlbWhite,
} = COLORS;

export const buildStateObject = (
    selected: Array<StrikeZoneSelectedProps|string> | undefined,
): object | undefined => {
    if(!selected) {
        return undefined;
    }
    return selected.reduce((acc, element) => {
        acc[typeof element === 'object' ? element.value : element] = true;
        return acc;
    }, {});
};

export const generateStyles = (
    isDark: boolean,
): StrikeZoneStyleProps => {
    return {
        strikeZoneEmptyStrokeColor: isDark ? darkStrikeZoneEmptyStrokeColor : mlbGrey4,
        strikeZoneEmptyFillColor: isDark ? mlbGrey7 : mlbGrey1,
        strikeZoneFillColor: linkBlueOnDark,
        strikeZoneFillStrokeColor: mlbWhite,
        homePlateFillColor: isDark ? darkHomePlateFillColor : mlbGrey3,
        strokeWidth,
        strikeZoneFocusFillColor: isDark ? mlbGrey6 : mlbGrey2,
        strikeZoneFocusStrokeColor: isDark ? mlbWhite : mlbGrey4,
    };
};

export const renderRectMatrix = ({
    rectMatrix,
    width,
    height,
    viewBoxWidth,
    viewBoxHeight,
    styles,
    selected,
    ariaLabel,
    selectable,
    showLabel,
}: StrikeZoneRenderRectMatrixProps): ReactElement => {
    const {
        strikeZoneEmptyFillColor,
        strikeZoneEmptyStrokeColor,
        strikeZoneFillColor,
        strikeZoneFillStrokeColor,
        strikeZoneFocusFillColor,
        strikeZoneFocusStrokeColor,
        strokeWidth,
    }: StrikeZoneStyleProps = styles;
    return (
        <StrikeZoneG
            $transform={`translate(${(viewBoxWidth - (width * rectMatrix[0].length)) / 2}px, ${(viewBoxHeight - (height * rectMatrix.length)) / 2}px)`}
        >
            {
                rectMatrix.map((row, index) => {
                    return (
                        <StrikeZoneG
                            key={`${row} ${index}`}
                            $transform={`translate(0px, ${index * height}px)`}
                        >
                            {
                                row.map((strikeZone, index) => {
                                    const {
                                        value,
                                        textTransform,
                                        type,
                                        textAnchor,
                                        dominantBaseline,
                                    } : StrikeZoneRectMatrixProps = strikeZone;
                                    const isSelected: boolean = selected ? selected[value] : false;
                                    const dataStrikeZoneProps: object = {};
                                    dataStrikeZoneProps[strikeZoneDataValueKey] = value;
                                    dataStrikeZoneProps[strikeZoneDataTypeKey] = type;
                                    return (
                                        <StrikeZoneG
                                            key={value}
                                            {...dataStrikeZoneProps}
                                            aria-label={`${ariaLabel ? ariaLabel + ' ' : ''}${value}${isSelected ? ' Selected' : ''}`}
                                            className={isSelected ? strikeZoneSelectedClassName : undefined}
                                            tabIndex={selectable ? 0 : undefined}
                                            $transform={`translate(${index * width}px, 0px)`}
                                            $focusFillColor={strikeZoneFocusFillColor}
                                            $focusStrokeColor={strikeZoneFocusStrokeColor}
                                            $selectable={selectable}
                                        >
                                            <StrikeZoneRect
                                                $width={width}
                                                $height={height}
                                                $fillColor={isSelected ? strikeZoneFillColor : strikeZoneEmptyFillColor}
                                                $strokeColor={strikeZoneEmptyStrokeColor}
                                                $strokeWidth={strokeWidth}
                                            />
                                            {
                                                showLabel &&
                                                <StrikeZoneText
                                                    $transform={textTransform}
                                                    $fillColor={isSelected ? strikeZoneFillStrokeColor : strikeZoneEmptyStrokeColor}
                                                    $textAnchor={textAnchor}
                                                    $dominantBaseline={dominantBaseline}
                                                >
                                                    {value}
                                                </StrikeZoneText>
                                            }
                                        </StrikeZoneG>
                                    );
                                })
                            }
                        </StrikeZoneG>
                    );
                })
            }
        </StrikeZoneG>
    );
};
